import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { camelCase } from 'lodash';
import { withNamespaces } from 'react-i18next';
import { AttachmentStatus, AttachmentType } from '../../utils/Constants';
import TicketsService from '../../services/TicketsService';
import { faFileAlt } from '@fortawesome/pro-light-svg-icons/faFileAlt';
import { faTimes } from '@fortawesome/pro-light-svg-icons/faTimes';

class Attachment extends Component {
  state = {
    status: '',
    idFile: 0,
    error: false,
    errorMsg: ''
  };

  constructor(props) {
    super(props);

    let initialStatus = props.isRequired ? AttachmentStatus.REQUIRED : AttachmentStatus.OPTIONAL;
    if (props.type === AttachmentType.DOWNLOAD) {
      initialStatus = AttachmentStatus.LOADED;
    }

    this.state = {
      status: initialStatus,
      idFile: 0,
      error: false,
      errorMsg: ''
    };
  }

  componentDidMount() {
    const { isRequired, file } = this.props;

    // If optional upload directly to backend
    if (!isRequired && file) {
      this.fileUpload(file);
    }
  }

  fileUpload = (file) => {
    const { idParent, onChange, id, maxNameLength } = this.props;
    const { maxSize } = this.props;
    const pattern = /application\/pdf|image\/jpeg|image\/png|application\/vnd.ms-excel|application\/vnd.openxmlformats-officedocument.spreadsheetml.sheet/;
    const reader = new FileReader();

    // Check file extension
    if (!file.type.match(pattern)) {
      this.setState({
        error: true,
        errorMsg: 'errorInvalidFileExt',
        status: AttachmentStatus.ERROR
      });
      return;
    }

    // Check file size
    if (file.size > maxSize) {
      this.setState({
        error: true,
        errorMsg: 'errorInvalidFileSize',
        status: AttachmentStatus.ERROR
      });
      return;
    }

    this.setState({
      status: AttachmentStatus.LOADING,
      error: false,
      errorMsg: ''
    });

    // Read file and send to backend
    reader.readAsDataURL(file);
    reader.onload = () => {
      const fileName = id.substr(0, maxNameLength);
      const promise = TicketsService.addTicketAttachment(idParent, reader.result, fileName);
      promise.then((data) => {
        this.setState({
          idFile: data,
          status: AttachmentStatus.LOADED,
          error: false,
          errorMsg: ''
        });
        onChange(this.inputFile.id, true);
      }, () => {
        reader.abort();
        this.setState({
          status: AttachmentStatus.ERROR,
          error: true,
          errorMsg: 'errorFileUpload'
        });
        onChange(this.inputFile.id, false);
      });
    };
  };

  handleChange = (event) => {
    const file = event.target.files[0];

    // User cancelled upload
    if (!file) {
      return;
    }

    this.fileUpload(file);
  };

  handleClick = (e) => {
    const { status, idFile } = this.state;
    const { isRequired, onChange, onRemove, type, id, name } = this.props;
    if (status === AttachmentStatus.LOADING) {
      e.preventDefault();
    }
    if (status === AttachmentStatus.LOADED) {
      e.preventDefault();
      if (type === AttachmentType.UPLOAD) {
        this.setState({
          idFile: 0,
          status: isRequired ? AttachmentStatus.REQUIRED : AttachmentStatus.OPTIONAL
        });
        TicketsService.removeTicketAttachment(idFile);
        this.inputFile.value = '';

        if (isRequired) {
          onChange(this.inputFile.id, false);
        } else {
          onRemove(this.inputFile.id);
        }
      } else {
        this.downloadTicketFile(id, name);
      }
    }
    if (status === AttachmentStatus.ERROR && !isRequired) {
      e.preventDefault();
      this.inputFile.value = '';
      onRemove(this.inputFile.id, false);
    }
  };

  downloadTicketFile = async (id, name) => {
    this.setState({
      status: AttachmentStatus.LOADING
    });

    try {
      const data = await TicketsService.getTicketAttachment(id);
      const { file } = data;
      const type = file.split(';')[0].split('/')[1];
      const fileName = `${camelCase(name)}-${id}.${type}`;

      const downloadLink = document.createElement('a');
      downloadLink.href = file;
      downloadLink.download = fileName;
      downloadLink.click();

      this.setState({
        status: AttachmentStatus.LOADED
      });
    } catch (error) {
      const { response: { data: { description = '' } = {} } } = error;
      const msgError = description || 'labelNetworkError';
      this.setState({
        status: AttachmentStatus.ERROR,
        error: true,
        errorMsg: msgError
      });
    }
  }

  getBoxStatusClasses = () => {
    const { status } = this.state;
    return classNames('attachment-status', {
      'bg-danger':
        [AttachmentStatus.REQUIRED].includes(status),
      'bg-success':
        [AttachmentStatus.LOADED].includes(status),
      'bg-warning':
        [AttachmentStatus.LOADING].includes(status),
      'bg-light':
        [AttachmentStatus.OPTIONAL].includes(status),
      'bg-red':
        [AttachmentStatus.ERROR].includes(status)
    });
  };

  getIconName = () => {
    const { status } = this.state;
    const { type } = this.props;
    if (status === AttachmentStatus.REQUIRED) {
      return 'arrow-up';
    }
    if (status === AttachmentStatus.LOADING) {
      return 'spinner-third';
    }
    if (status === AttachmentStatus.LOADED || status === AttachmentStatus.ERROR) {
      if (type === AttachmentType.DOWNLOAD) { return 'arrow-down'; }
      return 'times';
    }
    return 'times';
  };

  render() {
    const { name, acceptedFiles, t, showRequiredMsg, id, type } = this.props;
    const { error, errorMsg, idFile, status } = this.state;
    const iconName = this.getIconName();
    const withSpinner = status === AttachmentStatus.LOADING;

    return (
      <Fragment>
        <label
          className="attachment-box"
          htmlFor={`${camelCase(id)}Attachment`}
          onClick={this.handleClick}
        >
          {type === AttachmentType.UPLOAD && (
            <input
              ref={(ref) => {
                this.inputFile = ref;
              }}
              type="file"
              id={`${camelCase(id)}Attachment`}
              accept={acceptedFiles}
              onChange={this.handleChange}
            />
          )}
          <div className={this.getBoxStatusClasses()} />
          <FontAwesomeIcon
            icon={faFileAlt}
            size="lg"
            className="ml-2"
            style={{ color: "#004DD8" }}
          />
          <div className="attachment-name">
            {name.length >= 32 ? `${name.substr(0, 32)}...` : name}
          </div>
          {iconName && (
            <div className="attachment-icon with-pointer">
              <FontAwesomeIcon icon={faTimes} spin={withSpinner} size="lg" />
            </div>
          )}
        </label>
        {(error || (showRequiredMsg && !idFile)) && (
          <div className="error-attachment">
            {showRequiredMsg ? t("validationRequired") : t(errorMsg)}
          </div>
        )}
      </Fragment>
    );
  }
}

Attachment.defaultProps = {
  type: AttachmentType.UPLOAD,
  isRequired: false,
  acceptedFiles: 'image/jpeg,image/png,application/pdf',
  maxSize: 4194304,
  maxNameLength: 128
};

Attachment.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  name: PropTypes.string.isRequired,
  idParent: PropTypes.number,
  type: PropTypes.oneOf([AttachmentType.UPLOAD, AttachmentType.DOWNLOAD]),
  isRequired: PropTypes.bool,
  acceptedFiles: PropTypes.string,
  maxSize: PropTypes.number,
  onChange: PropTypes.func,
  onRemove: PropTypes.func,
  file: PropTypes.shape({}),
  maxNameLength: PropTypes.number,
};

export default withNamespaces()(Attachment);
